import React, { useEffect, useState } from 'react';
import {Alert, Button, Typography} from '@mui/joy';
import { useParams } from 'react-router-dom';
import Question from '../Question';
import { CssVarsProvider } from "@mui/joy";
import {createCustomTheme} from "./retheme";

const PersQuizPage = () => {
    const { quizDataUrl } = useParams();
    const [quizData, setQuizData] = useState(null);
    const [currentCardIndex, setCurrentCardIndex] = useState(0);
    const [selectedAnswer, setSelectedAnswer] = useState(null);
    const [correct, setCorrect] = useState(0);
    const [answered, setAnswered] = useState(0);
    const [score, setScore] = useState(null);
    const [scoreId, setScoreId] = useState(null);
    const theme = quizData?.color?.trim() ? createCustomTheme("#" + quizData.color) : null;

    const [showCorrect, setShowCorrect] = useState(false);

    // Fetch quiz data from the URL
    useEffect(() => {
        const fetchQuizData = async () => {
            try {
                if (!quizDataUrl) {
                    throw new Error('Invalid quizDataUrl');
                }
                const decodedUrl = decodeURIComponent(quizDataUrl);

                const isValidUrl = /^https?:\/\/.+$/.test(decodedUrl);
                if (!isValidUrl) {
                    throw new Error('Invalid URL format');
                }

                const response = await fetch(decodedUrl);
                if (!response.ok) {
                    throw new Error(`Network response was not ok: ${response.statusText}`);
                }

                const data = await response.json();
                setQuizData(data);
                console.log('Quiz data:', data);
            } catch (error) {
                console.error('Error fetching quiz data:', error);
            }
        };

        fetchQuizData();
    }, [quizDataUrl]);

    if (!quizData) {
        return <p>Loading...</p>;
    }

    const handleNextCard = () => {
        if (currentCardIndex === questions.length) {
            calculateScore();
        }
        setShowCorrect(false);
        setSelectedAnswer(null);
        setCurrentCardIndex((prevIndex) => prevIndex + 1);
    };

    const calculateScore = () => {
        const score = (correct / answered) * 100;
        setScore(score);
        calculateScoreAllotment();
    }

    const calculateScoreAllotment = () => {
        const scores = Object.entries(quizData.data.scores); // Get both IDs and score objects
        let bestMatch = null;

        scores.forEach(([id, scoreOption]) => {
            const scoreValue = parseInt(scoreOption.score, 10);
            if (scoreValue <= correct && (!bestMatch || scoreValue > parseInt(bestMatch.score, 10))) {
                bestMatch = { id, ...scoreOption }; // Include the ID in the best match
            }
        });

        if (bestMatch) {
            setScoreId(bestMatch.id); // Set the score ID to the group ID
        }
    };


    if (!quizData) {
        return <p>Loading...</p>;
    }

    const handleAnswerSelect = (answerKey) => {
        if (!showCorrect) {
            setSelectedAnswer(answerKey);
            setShowCorrect(true);
            const currentQuestion = questions[currentCardIndex - 1];
            const correctAnswers = Array.isArray(currentQuestion.correct)
                ? currentQuestion.correct
                : [currentQuestion.correct];

            if (correctAnswers.includes(answerKey)) {
                setCorrect(correct + 1);
            }
            setAnswered(answered + 1);
        }
    }

    const { title, description, data } = quizData;
    const questions = Object.values(data.questions);

    const progress = ((currentCardIndex) / questions.length) * 100;

    const content = (
        <div className={"pdiv"}>
            <div className={"pdiv2"}>
                {currentCardIndex === 0 && (
                    <div style={{display: "flex", justifyContent: "center", flexDirection: "column", textAlign: "center", justifyItems: "center"}}>
                        {quizData?.imageUrl && (
                            <img
                                src={quizData.imageUrl}
                                alt={quizData.imageUrl}
                                style={{ width: '50%', display: 'block', margin: 'auto' }}
                            />                        )}
                        <Typography level={"h1"}>{title}</Typography>
                        {description && (
                            <Typography level={"body-md"}>{description}</Typography>
                        )}
                        <Button onClick={handleNextCard}>Start Quiz</Button>
                    </div>
                )}

                {currentCardIndex > 0 && currentCardIndex <= questions.length && (
                    <div>
                        <Question
                            questionContents={questions[currentCardIndex - 1]}
                            selectedAnswer={selectedAnswer}
                            save={(answerKey) => {
                                handleAnswerSelect(answerKey);
                            }}
                            theme={theme}
                            displayCorrect={showCorrect}
                        />
                        <br/>
                        <Button
                            variant="solid"
                            color="primary"
                            disabled={!showCorrect}
                            onClick={() => {
                                if (showCorrect) {
                                    handleNextCard();
                                }
                            }}
                            sx={{
                                width: '100%',
                                position: 'relative',
                                height: 40,
                                backgroundColor: 'primary.500',
                                justifyContent: 'flex-start',
                                '&::before': {
                                    content: '""',
                                    position: 'absolute',
                                    left: 0,
                                    top: 0,
                                    bottom: 0,
                                    width: `${progress}%`,
                                    backgroundColor: 'primary.700',
                                    zIndex: 0,
                                    borderRadius: 'sm',
                                    transition: 'width 0.3s ease',
                                },
                                '& > *': {
                                    position: 'relative',
                                    zIndex: 1,
                                },
                            }}
                        >
                            <Typography
                                level="body-sm"
                                sx={{
                                    width: '100%',
                                    textAlign: 'center',
                                    color: showCorrect
                                        ? 'white !important'
                                        : progress > 55
                                            ? 'white !important'
                                            : 'grey !important',
                                }}
                            >
                                {showCorrect ? (
                                    "Next"
                                ) : (
                                    `${currentCardIndex} / ${questions.length}`
                                )}
                            </Typography>
                        </Button>
                    </div>
                )}

                {currentCardIndex > questions.length && (
                    <div className="card">
                        <Typography level={"h1"}>Quiz Complete!</Typography>
                        <Typography level={"body-md"} sx={{marginBottom: "5px"}}>Thank you for taking the quiz.</Typography>
                        {(() => {
                            const outcome = data.scores[scoreId];
                            if (!outcome) {
                                return <Alert color={"warning"}>Unable to determine the outcome. Please try again.</Alert>;
                            }
                            return (
                                <div>
                                    <Typography level={"h2"}>{outcome.title}</Typography>
                                    {(outcome?.description && outcome.description !== "") && (
                                        <Typography level={"body-md"}>{outcome.description}</Typography>
                                    )}
                                    {(outcome?.imageUrl && outcome.imageUrl !== "") && (
                                        <img src={outcome.imageUrl} alt={outcome.title} />
                                    )}
                                </div>
                            );
                        })()}
                        <Typography level={"h1"}>
                            {score?.toFixed(0)}%
                        </Typography>
                        <div style={{display: "flex", flexDirection: "column", alignItems: "center", marginTop: "20px"}}>
                            <Typography style={{color: "#B3B3B3"}} fontSize={"10px"}>Powered By</Typography>
                            <a
                                href={"https://online-interactives.vercel.app/"}
                                target="_blank"
                                rel="noopener noreferrer"
                                style={{display: "flex", flexDirection: "column", alignItems: "center", marginTop: "3px"}}
                            >
                                <img width={"20%"} src={"/cdn/logo/logo-watermark.svg"} alt={""}/>
                            </a>
                        </div>
                    </div>
                )}
            </div>
        </div>
    );

    return theme ? (
        <CssVarsProvider theme={theme}>
            {content}
        </CssVarsProvider>
    ) : (
        content
    );
};

export default PersQuizPage;